/**
 * Founder type identity system: codes, colors, taglines for shareable founder profiles.
 */

export const FOUNDER_TYPE_IDS = [
  'visionary',
  'executor',
  'innovator',
  'analyst',
  'strategist',
  'relationshipBuilder',
];

export const founderTypes = {
  visionary: {
    id: 'visionary',
    code: 'VIS',
    title: 'The Visionary',
    tagline: 'You see the future before others do.',
    shortDescription: 'Big-picture founder who inspires teams with bold direction and transformative ideas.',
    description:
      'You see possibilities where others don\'t. With a natural ability to envision the future, you inspire others with your bold ideas and transformative thinking.',
    strengths: [
      'Inspiring leadership',
      'Big-picture thinking',
      'Ability to spot future trends',
      'Creative problem-solving',
    ],
    challenges: [
      'May overlook details',
      'Can be impatient with execution',
      'Sometimes unrealistic expectations',
      'May pivot too frequently',
    ],
    actionPlan:
      'Partner with a detail-oriented executor to balance your visionary thinking. Schedule regular reflection time to focus on the big picture. Develop a system to validate ideas before full commitment.',
    compatibleWith: ['executor', 'analyst'],
    color: '#9B7BFF',
    colorDark: '#6C4FD4',
    famousFounders: ['Steve Jobs', 'Elon Musk', 'Oprah Winfrey'],
  },
  executor: {
    id: 'executor',
    code: 'EXE',
    title: 'The Executor',
    tagline: 'You turn vision into shipped reality.',
    shortDescription: 'Execution-first founder who builds systems, hits deadlines, and delivers consistently.',
    description:
      'You make things happen. With remarkable efficiency and practical thinking, you transform ideas into reality through disciplined execution and relentless follow-through.',
    strengths: [
      'Excellent at implementation',
      'Strong operational skills',
      'Reliable and consistent',
      'Process-oriented',
    ],
    challenges: [
      'May resist radical innovation',
      'Can be too focused on the "how" vs "why"',
      'Sometimes risk-averse',
      'May struggle with ambiguity',
    ],
    actionPlan:
      'Collaborate with visionaries to expand your thinking. Set aside time for strategic planning beyond execution. Practice comfort with ambiguity through small experiments.',
    compatibleWith: ['visionary', 'innovator'],
    color: '#00C9A7',
    colorDark: '#009E82',
    famousFounders: ['Sheryl Sandberg', 'Reed Hastings', 'Jeff Bezos (ops mode)'],
  },
  innovator: {
    id: 'innovator',
    code: 'INN',
    title: 'The Innovator',
    tagline: 'You find angles nobody else considers.',
    shortDescription: 'Creative founder who experiments fast, challenges assumptions, and builds novel solutions.',
    description:
      'You are a creative problem-solver who thrives on developing novel solutions. With your experimental mindset, you\'re constantly pushing boundaries and questioning assumptions.',
    strengths: [
      'Exceptional creativity',
      'Rapid idea generation',
      'Comfort with experimentation',
      'Ability to make unexpected connections',
    ],
    challenges: [
      'May struggle with focus',
      'Can chase too many ideas simultaneously',
      'Sometimes impractical solutions',
      'May resist structure',
    ],
    actionPlan:
      'Implement a system to evaluate and prioritize your innovations. Partner with executors to bring your best ideas to life. Create dedicated time for both ideation and refinement.',
    compatibleWith: ['executor', 'strategist'],
    color: '#FFB84D',
    colorDark: '#E69500',
    famousFounders: ['Brian Chesky', 'Dyson', 'Sara Blakely'],
  },
  analyst: {
    id: 'analyst',
    code: 'ANA',
    title: 'The Analyst',
    tagline: 'You decide with evidence, not hype.',
    shortDescription: 'Data-driven founder who cuts through noise with logic, patterns, and careful reasoning.',
    description:
      'You excel at making data-driven decisions. With your logical approach and attention to detail, you cut through complexity to find insights that others miss.',
    strengths: [
      'Data-driven decision making',
      'Critical thinking',
      'Risk assessment',
      'Pattern recognition',
    ],
    challenges: [
      'May experience analysis paralysis',
      'Can be overly cautious',
      'Sometimes miss emotional factors',
      'May struggle with intuitive leaps',
    ],
    actionPlan:
      'Set timeboxes for analysis to avoid overthinking. Partner with intuitive thinkers to balance your analytical approach. Practice making smaller decisions more quickly.',
    compatibleWith: ['visionary', 'relationshipBuilder'],
    color: '#4DA3FF',
    colorDark: '#1A7AD9',
    famousFounders: ['Warren Buffett', 'Bill Gates', 'Kat Cole'],
  },
  strategist: {
    id: 'strategist',
    code: 'STR',
    title: 'The Strategist',
    tagline: 'You see the game several moves ahead.',
    shortDescription: 'Systems thinker who positions, sequences, and wins through planning and timing.',
    description:
      'You excel at systematic planning and positioning. With your ability to see several moves ahead, you navigate complex competitive landscapes with clarity and purpose.',
    strengths: [
      'Long-term planning',
      'Competitive analysis',
      'Resource allocation',
      'Anticipating obstacles',
    ],
    challenges: [
      'May overcomplicate simple matters',
      'Can be slow to act',
      'Sometimes too focused on competitors',
      'May resist opportunistic pivots',
    ],
    actionPlan:
      'Balance strategic planning with action. Create regular review cycles to adapt strategies as conditions change. Partner with executors to maintain momentum.',
    compatibleWith: ['innovator', 'executor'],
    color: '#7B8CFF',
    colorDark: '#5568D4',
    famousFounders: ['Reid Hoffman', 'Andy Grove', 'Indra Nooyi'],
  },
  relationshipBuilder: {
    id: 'relationshipBuilder',
    slug: 'relationship-builder',
    code: 'REL',
    title: 'The Relationship Builder',
    tagline: 'You win through people, trust, and alignment.',
    shortDescription: 'People-first founder who builds networks, culture, and partnerships that compound.',
    description:
      'You create strong connections that power growth. With exceptional emotional intelligence, you build the networks, partnerships, and team culture that enable success.',
    strengths: [
      'Emotional intelligence',
      'Network development',
      'Team building',
      'Conflict resolution',
    ],
    challenges: [
      'May avoid necessary confrontation',
      'Can prioritize harmony over results',
      'Sometimes too trusting',
      'May neglect solo work',
    ],
    actionPlan:
      'Develop techniques for productive confrontation. Balance relationship building with focused execution time. Create objective criteria for evaluating partnerships.',
    compatibleWith: ['analyst', 'visionary'],
    color: '#FF6B9D',
    colorDark: '#D9447A',
    famousFounders: ['Richard Branson', 'Howard Schultz', 'Melinda Gates'],
  },
};

export const getFounderType = (typeId) => {
  if (!typeId) return null;
  const raw = String(typeId).trim();
  if (founderTypes[raw]) return founderTypes[raw];

  const normalized = raw.toLowerCase();
  return (
    Object.values(founderTypes).find(
      (type) =>
        type.id.toLowerCase() === normalized ||
        type.slug?.toLowerCase() === normalized
    ) || null
  );
};

export const getTypeProfilePath = (typeOrId) => {
  const type =
    typeof typeOrId === 'string' ? getFounderType(typeOrId) : typeOrId;
  if (!type) return '/types';
  return `/types/${type.slug || type.id}`;
};

export const getAllFounderTypes = () => FOUNDER_TYPE_IDS.map((id) => founderTypes[id]);
